/**
 * Synthetic Hurricane Helene (Sept 2024) demo data for the CFA feature flag.
 * Geometries approximate the Caribbean → Gulf / FL panhandle corridor; not NHC
 * official.
 */

export const HISTORICAL_HURRICANES_LAYER_ID = 'cfa-historical-hurricanes';
export const HISTORICAL_HURRICANES_LAYER_TITLE = 'Historical Hurricanes';
export const CFA_HELENE_STORES_LAYER_ID = 'cfa-helene-stores';
export const CFA_HELENE_STORES_LAYER_TITLE = 'CFA Stores (Helene demo)';
export const EPA_GULF_FACILITIES_LAYER_ID = 'epa-gulf-facilities';
export const EPA_GULF_FACILITIES_LAYER_TITLE = 'EPA Gulf facilities';

export type SaffirSimpsonCategory =
  | 'TD'
  | 'TS'
  | 'Cat1'
  | 'Cat2'
  | 'Cat3'
  | 'Cat4'
  | 'Cat5';

export type HeleneAdvisoryPoint = {
  id: string;
  label: string;
  datetime: string;
  lon: number;
  lat: number;
  maxWindMph: number;
  category: SaffirSimpsonCategory;
};

export type CfaDemoStore = {
  id: string;
  storeNumber: string;
  name: string;
  city: string;
  state: string;
  lon: number;
  lat: number;
  inCone: boolean;
};

/**
 * Approximate forecast cone (outer ring) for mid-late Sept 2024 Helene
 * approach.
 */
export const HELENE_CONE_RING: Array<[number, number]> = [
  [-90.2, 23.5],
  [-88.5, 24.0],
  [-86.0, 25.2],
  [-84.0, 26.8],
  [-82.5, 28.5],
  [-81.8, 30.2],
  [-81.5, 31.8],
  [-81.8, 33.2],
  [-82.5, 34.5],
  [-84.0, 35.2],
  [-86.0, 35.0],
  [-87.5, 34.0],
  [-88.8, 32.5],
  [-89.5, 30.5],
  [-90.0, 28.0],
  [-90.5, 25.5],
  [-90.2, 23.5],
];

/** Observed / forecast track through advisory points. */
export const HELENE_ADVISORIES: HeleneAdvisoryPoint[] = [
  {
    id: 'helene-adv-1',
    label: 'Tropical Depression Nine',
    datetime: '2024-09-22T18:00:00.000Z',
    lon: -82.8,
    lat: 15.2,
    maxWindMph: 35,
    category: 'TD',
  },
  {
    id: 'helene-adv-2',
    label: 'Tropical Storm Helene',
    datetime: '2024-09-24T06:00:00.000Z',
    lon: -84.5,
    lat: 17.8,
    maxWindMph: 60,
    category: 'TS',
  },
  {
    id: 'helene-adv-3',
    label: 'Hurricane Helene (Cat 1)',
    datetime: '2024-09-25T12:00:00.000Z',
    lon: -86.2,
    lat: 20.5,
    maxWindMph: 80,
    category: 'Cat1',
  },
  {
    id: 'helene-adv-4',
    label: 'Hurricane Helene (Cat 2)',
    datetime: '2024-09-26T00:00:00.000Z',
    lon: -87.0,
    lat: 23.0,
    maxWindMph: 105,
    category: 'Cat2',
  },
  {
    id: 'helene-adv-5',
    label: 'Hurricane Helene (Cat 3)',
    datetime: '2024-09-26T12:00:00.000Z',
    lon: -86.5,
    lat: 25.8,
    maxWindMph: 120,
    category: 'Cat3',
  },
  {
    id: 'helene-adv-6',
    label: 'Hurricane Helene (Cat 4)',
    datetime: '2024-09-26T18:00:00.000Z',
    lon: -85.2,
    lat: 27.8,
    maxWindMph: 140,
    category: 'Cat4',
  },
  {
    id: 'helene-adv-7',
    label: 'Hurricane Helene (landfall approach)',
    datetime: '2024-09-27T00:00:00.000Z',
    lon: -84.0,
    lat: 29.5,
    maxWindMph: 130,
    category: 'Cat4',
  },
  {
    id: 'helene-adv-8',
    label: 'Hurricane Helene (inland)',
    datetime: '2024-09-27T12:00:00.000Z',
    lon: -83.5,
    lat: 32.0,
    maxWindMph: 75,
    category: 'Cat1',
  },
];

export const CFA_HELENE_STORES: CfaDemoStore[] = [
  {
    id: 'cfa-store-tallahassee',
    storeNumber: '1234',
    name: 'Chick-fil-A Tallahassee – Capital Circle',
    city: 'Tallahassee',
    state: 'FL',
    lon: -84.28,
    lat: 30.44,
    inCone: true,
  },
  {
    id: 'cfa-store-jacksonville',
    storeNumber: '1288',
    name: 'Chick-fil-A Jacksonville – Southside',
    city: 'Jacksonville',
    state: 'FL',
    lon: -81.55,
    lat: 30.25,
    inCone: true,
  },
  {
    id: 'cfa-store-valdosta',
    storeNumber: '1412',
    name: 'Chick-fil-A Valdosta – St. Augustine Rd',
    city: 'Valdosta',
    state: 'GA',
    lon: -83.28,
    lat: 30.83,
    inCone: true,
  },
  {
    id: 'cfa-store-albany',
    storeNumber: '1550',
    name: 'Chick-fil-A Albany – Dawson Rd',
    city: 'Albany',
    state: 'GA',
    lon: -84.16,
    lat: 31.58,
    inCone: true,
  },
  {
    id: 'cfa-store-savannah',
    storeNumber: '1620',
    name: 'Chick-fil-A Savannah – Abercorn',
    city: 'Savannah',
    state: 'GA',
    lon: -81.1,
    lat: 32.0,
    inCone: true,
  },
  {
    id: 'cfa-store-augusta',
    storeNumber: '1705',
    name: 'Chick-fil-A Augusta – Washington Rd',
    city: 'Augusta',
    state: 'GA',
    lon: -82.02,
    lat: 33.47,
    inCone: true,
  },
  {
    id: 'cfa-store-charleston',
    storeNumber: '1833',
    name: 'Chick-fil-A Charleston – Mt Pleasant',
    city: 'Charleston',
    state: 'SC',
    lon: -79.83,
    lat: 32.82,
    inCone: false,
  },
  {
    id: 'cfa-store-orlando',
    storeNumber: '1901',
    name: 'Chick-fil-A Orlando – International Dr',
    city: 'Orlando',
    state: 'FL',
    lon: -81.47,
    lat: 28.45,
    inCone: false,
  },
  {
    id: 'cfa-store-tampa',
    storeNumber: '2010',
    name: 'Chick-fil-A Tampa – Westshore',
    city: 'Tampa',
    state: 'FL',
    lon: -82.53,
    lat: 27.95,
    inCone: false,
  },
  {
    id: 'cfa-store-atlanta',
    storeNumber: '2104',
    name: 'Chick-fil-A Atlanta – Midtown',
    city: 'Atlanta',
    state: 'GA',
    lon: -84.38,
    lat: 33.78,
    inCone: false,
  },
];

export function storesInCone(): CfaDemoStore[] {
  return CFA_HELENE_STORES.filter((s) => s.inCone);
}

export function formatStoreListLabel(store: CfaDemoStore): string {
  return `${store.city} #${store.storeNumber}`;
}

/** Saffir–Simpson-ish fill colors for advisory points (RGBA 0–255). */
export const CATEGORY_COLORS: Record<
  SaffirSimpsonCategory,
  [number, number, number, number]
> = {
  TD: [90, 180, 90, 1],
  TS: [40, 140, 220, 1],
  Cat1: [255, 230, 80, 1],
  Cat2: [255, 170, 40, 1],
  Cat3: [255, 100, 40, 1],
  Cat4: [220, 40, 40, 1],
  Cat5: [160, 40, 160, 1],
};

export const HELENE_STORE_CHECKLIST_LABELS = [
  'Secure outdoor seating / patio furniture',
  'Confirm generator / cold-chain readiness',
  'Brief shift leads on early close criteria',
  'Report status to Regional Ops by next advisory',
] as const;
